import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { noop, Subject } from 'rxjs';
import { NotificationResponse, MetaPagination } from '../../../../api';
import { BaseButtonSize } from '../../../shared/components/button/base-button.component';
import { baseSharedActions, BaseCoreState } from '../../../_store';
import { BaseModalService } from '../../../shared/services/base-modal.service';
import { parseRelativeUrl } from '../../../shared/helpers/base-parse-relative-url';

@Component({
  selector: 'base-notifications-modal',
  templateUrl: './base-notifications-modal.component.html',
})
export class BaseNotificationsModalComponent implements OnInit, OnDestroy {
  @ViewChild('notificationsContainer', { static: true }) notificationsContainer: ElementRef;

  @Input() modalRef: NgbModalRef;
  @Input() activeNotificationId?: number;
  @Input() allNotifications: Array<NotificationResponse> = [];
  @Input() allNotificationsRead = false;
  @Input()
  set pagination(value: MetaPagination | undefined) {
    this._pagination = value;

    if (value?.page) {
      const { current, size, itemsCount } = value.page;

      this.page = current ?? this.page ?? 1;
      this.size = size ?? this.size ?? 20;
      this.itemsCount = itemsCount ?? this.itemsCount ?? 0;
    }
  }

  buttonSize = BaseButtonSize;
  page = 0;
  size = 0;
  itemsCount = 0;

  private _pagination?: MetaPagination;
  private destroy$ = new Subject<void>();

  constructor(private store: Store<BaseCoreState>, private router: Router, private modalService: BaseModalService) {}

  get pagination(): MetaPagination | undefined {
    return this._pagination;
  }

  ngOnInit() {
    this.modalRef.shown.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.scrollToActiveNotification();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPageChange(value: number): void {
    if (value !== this.page) {
      this.page = value;
      this.store.dispatch(baseSharedActions.loadUserNotifications({ payload: { page: this.page, size: this.size } }));
    }
    this.notificationsContainer.nativeElement.scrollIntoView();
  }

  markNotificationAsRead(event: Event, notification: NotificationResponse): void {
    event.preventDefault();

    const urlData = parseRelativeUrl(notification.context?.url || notification.messageBody?.match(/href="(.*)"/)?.[1]);

    if (urlData) {
      this.navigateTo(urlData, notification);
    } else {
      if (!notification.isRead) {
        this.markRead(notification.id);
      }
    }
  }

  onMarkAllRead(): void {
    const confirmationModalRef = this.modalService.open(
      {
        headText: 'notification.confirmationModal.headText',
        bodyText: 'notification.confirmationModal.bodyText',
        okText: 'action.okProceed',
      },
      {
        scrollable: true,
        backdrop: 'static',
        size: 'md',
      }
    );

    confirmationModalRef.result.then(() => {
      this.store.dispatch(baseSharedActions.setAllUserNotificationsAsRead());
    }, noop);
  }

  closeModal(): void {
    this.store.dispatch(baseSharedActions.loadUserNotifications({ payload: { isRead: false } }));
    this.modalRef.close();
  }

  trackByNotification(_: number, item: NotificationResponse) {
    return item.id;
  }

  private navigateTo(
    url: { path: string; queryParams: Record<string, any> },
    { isRead, id }: NotificationResponse
  ): void {
    this.router.navigate([url.path], { queryParams: url.queryParams }).then(() => {
      if (!isRead) {
        this.markRead(id);
      }
      this.modalRef.close();
    });
  }

  private scrollToActiveNotification() {
    if (this.activeNotificationId) {
      const elem = document.getElementById(`n${this.activeNotificationId}`);

      if (elem) {
        elem.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  private markRead(notificationId: number): void {
    this.store.dispatch(
      baseSharedActions.setNotificationReadState({
        payload: { notification: notificationId },
        page: this.page,
        size: this.size,
      })
    );
  }
}
